import { useNavigate } from "react-router-dom";
import { compressImage } from "@/lib/imageCompress";
import { Navbar, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { filterCategories, filterProvinces, filterSpecies, categoryToListingType } from "@/lib/filterData";
import { X, ImageIcon, Loader2, Search, Lock } from "lucide-react";
import { BackButton } from "@/components/BackButton";
import { AddressAutocompleteInput } from "@/components/AddressAutocompleteInput";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

const unitLabels: Record<string, string> = {
  trad: "kr/träd",
  plankor: "kr/planka",
  ved: "kr/kubik",
};

const priceTextOptions = ["kr/kvm", "kr/kg", "kr/planka", "kr/träd", "kr/säck"] as const;

type Platform = "barka" | "blocket" | "fb_marketplace";

// Flatten kommuner with their provinces for the searchable selector
const allKommuner: { kommun: string; province: string }[] = filterProvinces
  .flatMap((p) => p.municipalities.map((m) => ({ kommun: m, province: p.name })))
  .sort((a, b) => a.kommun.localeCompare(b.kommun, "sv"));

export default function AdminUploadListing() {
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [subCategory, setSubCategory] = useState("");
  const [species, setSpecies] = useState("");
  const [woodAge, setWoodAge] = useState("");
  const [diameter, setDiameter] = useState("");
  const [moistureContent, setMoistureContent] = useState("");
  const [processingGrade, setProcessingGrade] = useState("");
  const [province, setProvince] = useState("");
  const [municipality, setMunicipality] = useState("");
  const [kommunSearch, setKommunSearch] = useState("");
  const [kommunOpen, setKommunOpen] = useState(false);
  const [price, setPrice] = useState("");
  const [priceText, setPriceText] = useState<string>(""); // empty = (ingen)
  const [quantity, setQuantity] = useState("");
  const [pickupEnabled, setPickupEnabled] = useState(true);
  const [shippingEnabled, setShippingEnabled] = useState(false);
  const [shippingPrice, setShippingPrice] = useState("");
  const [deliveryRadiusKm, setDeliveryRadiusKm] = useState("");
  const [pickupAddress, setPickupAddress] = useState("");
  const [plankLength, setPlankLength] = useState("");
  const [plankWidth, setPlankWidth] = useState("");
  const [plankHeight, setPlankHeight] = useState("");
  const [images, setImages] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [maxImagesHint, setMaxImagesHint] = useState(false);

  const [platform, setPlatform] = useState<Platform>("barka");
  const [externalUrl, setExternalUrl] = useState("");
  const [urlError, setUrlError] = useState("");

  const isAdmin = hasRole("admin");

  const selectedCategory = filterCategories.find((c) => c.value === category);
  const subOptions = selectedCategory?.subs ?? [];

  const listingType = (categoryToListingType[category] ?? "trad") as "trad" | "plankor" | "ved";
  const unit = unitLabels[listingType] || "kr";
  const priceNum = parseFloat(price) || 0;
  const quantityNum = parseInt(quantity) || 0;
  const total = priceNum * quantityNum;

  // Filtered kommun suggestions
  const kommunMatches = useMemo(() => {
    const q = kommunSearch.trim().toLowerCase();
    if (!q) return allKommuner.slice(0, 50);
    return allKommuner
      .filter((k) => k.kommun.toLowerCase().includes(q))
      .slice(0, 50);
  }, [kommunSearch]);

  const uploadBlobs = async (blobs: Blob[]) => {
    if (!user) return;
    const remaining = 6 - images.length;
    if (remaining <= 0) {
      setMaxImagesHint(true);
      window.setTimeout(() => setMaxImagesHint(false), 3000);
      return;
    }
    setUploading(true);
    const newUrls: string[] = [];
    for (const blob of blobs.slice(0, remaining)) {
      try {
        const { blob: compressed, ext, contentType } = await compressImage(blob);
        const fileName = `${user.id}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
        const { error } = await supabase.storage.from("listing-images").upload(fileName, compressed, { contentType });
        if (error) throw error;
        const { data: urlData } = supabase.storage.from("listing-images").getPublicUrl(fileName);
        newUrls.push(urlData.publicUrl);
      } catch (err: any) {
        toast.error("Kunde inte ladda upp bild: " + err.message);
      }
    }
    setImages((prev) => [...prev, ...newUrls]);
    setUploading(false);
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    await uploadBlobs(Array.from(files));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Paste image support
  useEffect(() => {
    if (!isAdmin) return;
    const onPaste = async (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const blobs: Blob[] = [];
      for (const item of Array.from(items)) {
        if (item.kind === "file" && item.type.startsWith("image/")) {
          const f = item.getAsFile();
          if (f) blobs.push(f);
        }
      }
      if (blobs.length === 0) return; // silent
      e.preventDefault();
      if (images.length >= 6) {
        setMaxImagesHint(true);
        window.setTimeout(() => setMaxImagesHint(false), 3000);
        return;
      }
      await uploadBlobs(blobs);
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images.length, isAdmin, user?.id]);

  const removeImage = (index: number) => setImages((prev) => prev.filter((_, i) => i !== index));

  const selectKommun = (k: { kommun: string; province: string }) => {
    setMunicipality(k.kommun);
    setProvince(k.province);
    setKommunSearch(k.kommun);
    setKommunOpen(false);
  };

  const clearKommun = () => {
    setMunicipality("");
    setProvince("");
    setKommunSearch("");
  };

  const handlePublish = async () => {
    if (!user) return;
    setUrlError("");
    if ((platform === "blocket" || platform === "fb_marketplace") && !externalUrl.trim()) {
      setUrlError("Ange en länk till den externa annonsen.");
      return;
    }

    setSaving(true);
    const deliveryValue = pickupEnabled && shippingEnabled ? "båda" : shippingEnabled ? "leverans" : pickupEnabled ? "hämta" : "hämta";

    const payload: any = {
      seller_id: user.id,
      title: title.trim() || "Annons",
      description: description || null,
      category: category || "ved",
      sub_category: subCategory || null,
      species: species || "Övrigt",
      listing_type: listingType,
      wood_age: woodAge ? parseInt(woodAge) : null,
      diameter: diameter || null,
      moisture_content: moistureContent || null,
      processing_grade: processingGrade || null,
      province: province || null,
      municipality: municipality || null,
      location: municipality || null,
      price: priceNum,
      price_unit: priceText || null,
      quantity: quantityNum || null,
      pickup_enabled: pickupEnabled,
      shipping_enabled: shippingEnabled,
      shipping_price: shippingPrice ? parseFloat(shippingPrice) : null,
      delivery_radius_km: deliveryRadiusKm ? parseFloat(deliveryRadiusKm) : null,
      pickup_address: pickupEnabled && pickupAddress.trim() ? pickupAddress.trim() : null,
      plank_length_cm: listingType === "plankor" && plankLength ? parseFloat(plankLength) : null,
      plank_width_cm: listingType === "plankor" && plankWidth ? parseFloat(plankWidth) : null,
      plank_height_cm: listingType === "plankor" && plankHeight ? parseFloat(plankHeight) : null,
      delivery: deliveryValue,
      images,
      status: "aktiv",
      uploaded_by_admin: true,
      platform_source: platform,
      external_url: platform === "barka" ? null : externalUrl.trim(),
    };

    const { error } = await (supabase as any).from("listings").insert(payload);
    setSaving(false);
    if (error) {
      toast.error("Kunde inte publicera: " + error.message);
      return;
    }
    toast.success("Annonsen har publicerats.");
    navigate("/admin");
  };

  if (!isAdmin) {
    return (
      <div className="flex min-h-screen flex-col">
        <Navbar />
        <div className="container flex-1 py-20 text-center">
          <p className="text-lg font-medium">Åtkomst nekad</p>
        </div>
        <Footer hideOnMobile />
      </div>
    );
  }

  // ---------- Sections (rendered into left/right columns) ----------

  const ImagesSection = (
    <section>
      <h2 className="mb-4 text-lg font-semibold text-foreground">Bilder</h2>
      {images.length > 0 && (
        <div className="mb-4 flex gap-3 overflow-x-auto pb-2 py-[10px]">
          {images.map((url, i) => (
            <div key={i} className="relative shrink-0">
              <img src={url} alt={`Bild ${i + 1}`} className="h-24 w-24 rounded-lg object-cover" />
              <button
                onClick={() => removeImage(i)}
                className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow-sm"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      {images.length >= 6 ? (
        <p className="text-sm text-muted-foreground">Du har uppnått maxgränsen på 6 bilder.</p>
      ) : (
        <>
          <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" />
          <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
            {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ImageIcon className="mr-2 h-4 w-4" />}
            Lägg till bilder
          </Button>
          <p className="mt-2 text-xs text-muted-foreground">
            Du kan också klistra in bilder direkt med Ctrl+V / Cmd+V
          </p>
        </>
      )}
      {maxImagesHint && (
        <p className="mt-2 text-sm text-destructive">Du har uppnått maxgränsen på 6 bilder.</p>
      )}
    </section>
  );

  const BasicInfoSection = (
    <section>
      <h2 className="mb-4 text-lg font-semibold text-foreground">Grundinformation</h2>
      <div className="space-y-4">
        <div>
          <Label>Titel</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Annonstitel" />
        </div>
        <div>
          <Label>Beskrivning</Label>
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Beskriv annonsen..." rows={4} />
        </div>
      </div>
    </section>
  );

  const CategorySection = (
    <section>
      <h2 className="mb-4 text-lg font-semibold text-foreground">Kategori</h2>
      <div className="space-y-4">
        <div>
          <Label>Kategori</Label>
          <Select value={category} onValueChange={(v) => { setCategory(v); setSubCategory(""); }}>
            <SelectTrigger><SelectValue placeholder="Välj kategori" /></SelectTrigger>
            <SelectContent>
              {filterCategories.map((c) => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {subOptions.length > 0 && (
          <div>
            <Label>Underkategori <span className="text-muted-foreground font-normal">(valfritt)</span></Label>
            <Select value={subCategory || "__none__"} onValueChange={(v) => setSubCategory(v === "__none__" ? "" : v)}>
              <SelectTrigger><SelectValue placeholder="Välj underkategori" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Ingen</SelectItem>
                {subOptions.slice().sort((a, b) => a.localeCompare(b, "sv")).map((sub) => (
                  <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    </section>
  );

  const SpeciesSection = (
    <section>
      <h2 className="mb-4 text-lg font-semibold text-foreground">Trädslag</h2>
      <Select value={species} onValueChange={setSpecies}>
        <SelectTrigger><SelectValue placeholder="Välj trädslag" /></SelectTrigger>
        <SelectContent>
          {filterSpecies.slice().sort((a, b) => a.localeCompare(b, "sv")).map((s) => (
            <SelectItem key={s} value={s}>{s}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </section>
  );

  const WoodInfoSection = (
    <section>
      <h2 className="mb-4 text-lg font-semibold text-foreground">Träinformation</h2>
      <div className="space-y-4">
        <div>
          <Label>Ålder (år)</Label>
          <Input type="number" value={woodAge} onChange={(e) => setWoodAge(e.target.value)} placeholder="T.ex. 30" />
        </div>
        {listingType === "trad" && (
          <div>
            <Label>Genomsnittlig diameter (cm)</Label>
            <Input value={diameter} onChange={(e) => setDiameter(e.target.value)} placeholder="T.ex. 25" />
          </div>
        )}
        {(listingType === "plankor" || listingType === "ved") && (
          <div>
            <Label>Fuktkvot</Label>
            <Select value={moistureContent} onValueChange={setMoistureContent}>
              <SelectTrigger><SelectValue placeholder="Välj fuktkvot" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="< 16-18%">{"< 16-18%"}</SelectItem>
                <SelectItem value="> 20%">{"> 20%"}</SelectItem>
                <SelectItem value="> 24-30%">{"> 24-30%"}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {listingType === "plankor" && (
          <>
            <div>
              <Label>Bearbetningsgrad</Label>
              <Select value={processingGrade} onValueChange={setProcessingGrade}>
                <SelectTrigger><SelectValue placeholder="Välj bearbetningsgrad" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Hyvlat">Hyvlat</SelectItem>
                  <SelectItem value="Råsågat">Råsågat</SelectItem>
                  <SelectItem value="Kantat">Kantat</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Dimensioner (cm)</Label>
              <div className="mt-1.5 grid grid-cols-3 gap-2">
                <Input type="number" inputMode="decimal" value={plankLength} onChange={(e) => setPlankLength(e.target.value)} placeholder="Längd" />
                <Input type="number" inputMode="decimal" value={plankWidth} onChange={(e) => setPlankWidth(e.target.value)} placeholder="Bredd" />
                <Input type="number" inputMode="decimal" value={plankHeight} onChange={(e) => setPlankHeight(e.target.value)} placeholder="Höjd" />
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );

  const LocationSection = (
    <section>
      <h2 className="mb-4 text-lg font-semibold text-foreground">Plats</h2>
      <div className="space-y-4">
        <div className="relative">
          <Label>Kommun</Label>
          <div className="relative mt-1.5">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={kommunSearch}
              onChange={(e) => {
                setKommunSearch(e.target.value);
                setKommunOpen(true);
                if (e.target.value.trim() === "") {
                  setMunicipality("");
                  setProvince("");
                }
              }}
              onFocus={() => setKommunOpen(true)}
              onBlur={() => window.setTimeout(() => setKommunOpen(false), 150)}
              placeholder="Sök efter kommun..."
              className="pl-9"
            />
            {municipality && (
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); clearKommun(); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:bg-accent"
                aria-label="Rensa kommun"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          {kommunOpen && kommunMatches.length > 0 && (
            <div className="absolute z-50 mt-1 max-h-72 w-full overflow-y-auto rounded-md border border-border bg-popover shadow-lg">
              {kommunMatches.map((k) => (
                <button
                  key={`${k.province}-${k.kommun}`}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); selectKommun(k); }}
                  className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-accent"
                >
                  <span className="font-medium text-foreground">{k.kommun}</span>
                  <span className="text-xs text-muted-foreground">{k.province}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <Label className="text-muted-foreground">Landskap</Label>
          <div className="relative mt-1.5">
            <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={province}
              readOnly
              placeholder="Fylls i automatiskt"
              className="pl-9 bg-muted/50 text-muted-foreground cursor-not-allowed"
            />
          </div>
        </div>
      </div>
    </section>
  );

  const PriceSection = (
    <section>
      <h2 className="mb-4 text-lg font-semibold text-foreground">Pris & Kvantitet</h2>
      <div className="space-y-4">
        <div>
          <Label>Pris (kr)</Label>
          <Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0" />
        </div>
        <div>
          <Label>Pristext <span className="text-muted-foreground font-normal">(valfritt)</span></Label>
          <Select value={priceText || "__none__"} onValueChange={(v) => setPriceText(v === "__none__" ? "" : v)}>
            <SelectTrigger><SelectValue placeholder="(ingen)" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">(ingen)</SelectItem>
              {priceTextOptions.map((p) => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Antal / Mängd</Label>
          <Input type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="0" />
        </div>
      </div>
      <div className="mt-4 rounded-xl border border-border bg-card p-4 text-center">
        <p className="text-sm text-muted-foreground">Totalt värde</p>
        <p className="text-3xl font-bold text-foreground">{total.toLocaleString("sv-SE")} kr</p>
        {priceNum > 0 && (
          <p className="mt-1 text-sm text-muted-foreground">
            {priceNum.toLocaleString("sv-SE")} kr{priceText ? ` ${priceText}` : ""}
            {!priceText && unit !== "kr" ? ` (${unit})` : ""}
          </p>
        )}
      </div>
    </section>
  );

  const PlatformSection = (
    <section>
      <h2 className="mb-4 text-lg font-semibold text-foreground">Välj köpplattform</h2>
      <div className="space-y-3">
        {([
          { value: "barka", label: "Timbernet", desc: "Standard – köpknappen öppnar Timbernets köpförslag." },
          { value: "blocket", label: "Blocket", desc: "Köpknappen länkar vidare till Blocket-annonsen." },
          { value: "fb_marketplace", label: "FB Marketplace", desc: "Köpknappen länkar vidare till Facebook Marketplace." },
        ] as { value: Platform; label: string; desc: string }[]).map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => { setPlatform(opt.value); setUrlError(""); }}
            className={`w-full text-left rounded-lg border p-4 transition-colors ${
              platform === opt.value
                ? "border-primary bg-primary/5 ring-1 ring-primary"
                : "border-border bg-card hover:bg-accent/40"
            }`}
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="font-medium text-foreground">{opt.label}</p>
                <p className="text-xs text-muted-foreground">{opt.desc}</p>
              </div>
              <div className={`h-4 w-4 rounded-full border-2 ${platform === opt.value ? "border-primary bg-primary" : "border-muted-foreground/40"}`} />
            </div>
          </button>
        ))}
      </div>

      {platform === "blocket" && (
        <div className="mt-4">
          <Label>Blocket-länk</Label>
          <Input
            value={externalUrl}
            onChange={(e) => { setExternalUrl(e.target.value); setUrlError(""); }}
            placeholder="https://www.blocket.se/annons/..."
          />
          {urlError && <p className="mt-1 text-sm text-destructive">{urlError}</p>}
        </div>
      )}
      {platform === "fb_marketplace" && (
        <div className="mt-4">
          <Label>Facebook Marketplace-länk</Label>
          <Input
            value={externalUrl}
            onChange={(e) => { setExternalUrl(e.target.value); setUrlError(""); }}
            placeholder="https://www.facebook.com/marketplace/item/..."
          />
          {urlError && <p className="mt-1 text-sm text-destructive">{urlError}</p>}
        </div>
      )}
    </section>
  );

  const DeliverySection = (
    <section>
      <h2 className="mb-4 text-lg font-semibold text-foreground">Leverans</h2>
      <div className="space-y-4">
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-foreground">Upphämtning</p>
              <p className="text-xs text-muted-foreground">Köparen hämtar på plats</p>
            </div>
            <Switch checked={pickupEnabled} onCheckedChange={setPickupEnabled} />
          </div>
          {pickupEnabled && (
            <div className="mt-4 space-y-2 border-t border-border pt-4">
              <Label htmlFor="admin-pickup-address">Upphämtningsadress</Label>
              <AddressAutocompleteInput
                id="admin-pickup-address"
                value={pickupAddress}
                onChange={setPickupAddress}
                placeholder="Ange adressen där virket hämtas"
              />
            </div>
          )}
        </div>

        <div className="rounded-lg border border-border bg-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-foreground">Frakt</p>
              <p className="text-xs text-muted-foreground">Säljaren levererar</p>
            </div>
            <Switch checked={shippingEnabled} onCheckedChange={setShippingEnabled} />
          </div>
          {shippingEnabled && (
            <div className="mt-4 space-y-3 border-t border-border pt-4">
              <div>
                <Label>Fraktpris (kr)</Label>
                <Input type="number" value={shippingPrice} onChange={(e) => setShippingPrice(e.target.value)} placeholder="0" />
              </div>
              <div>
                <Label>Leveransradius (km)</Label>
                <Input type="number" value={deliveryRadiusKm} onChange={(e) => setDeliveryRadiusKm(e.target.value)} placeholder="T.ex. 50" />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );

  const sectionWrap = "rounded-xl border border-border bg-card/40 p-5";

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Navbar />
      <div className="container max-w-7xl flex-1 py-8">
        <BackButton fallback="/admin" />

        <h1 className="mb-8 font-display text-2xl font-bold text-foreground">Ladda upp annons</h1>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-10">
          {/* Left column */}
          <div className="space-y-6">
            <div className={sectionWrap}>{ImagesSection}</div>
            <div className={sectionWrap}>{BasicInfoSection}</div>
            <div className={sectionWrap}>{CategorySection}</div>
            <div className={sectionWrap}>{SpeciesSection}</div>
            <div className={sectionWrap}>{WoodInfoSection}</div>
            <div className={sectionWrap}>{LocationSection}</div>
          </div>

          {/* Right column */}
          <div className="space-y-6">
            <div className={sectionWrap}>{PriceSection}</div>
            <div className={sectionWrap}>{PlatformSection}</div>
            <div className={sectionWrap}>{DeliverySection}</div>
          </div>
        </div>

        <Separator className="my-8" />

        <div className="space-y-3 pb-8">
          <Button
            className="w-full bg-primary hover:bg-primary/90"
            size="lg"
            disabled={saving}
            onClick={handlePublish}
          >
            {saving ? "Publicerar…" : "Publicera annons"}
          </Button>
          <Button variant="outline" className="w-full" size="lg" onClick={() => navigate("/admin")}>
            Avbryt
          </Button>
        </div>
      </div>
      <Footer hideOnMobile />
    </div>
  );
}
